'use client';

import { useRef, useState, type PointerEvent } from 'react';

const IMAGE_CDN_BASE = (process.env.NEXT_PUBLIC_IMAGE_CDN_URL || '').replace(/\/$/, '');
/** Bump when story photos are replaced on the CDN under the same filenames. */
const STORY_IMAGE_VERSION = '20261005';

function storyImageUrl(path: string) {
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const separator = normalized.includes('?') ? '&' : '?';
  return `${IMAGE_CDN_BASE}${normalized}${separator}v=${STORY_IMAGE_VERSION}`;
}

const STORY_COPY = [
  {
    image: '/assets/img/snehal-mangesh.jpg',
    name: 'Snehal & Mangesh',
    married: 'Married in 2019',
    quote:
      'The community-focused approach made all the difference. We connected with families who shared our values and traditions.',
  },
  {
    image: '/assets/img/ravikant-netra.jpg',
    name: 'Netra & Ravikant',
    married: 'Married in 2017',
    quote:
      'They helped us find each other. The cultural compatibility matching was perfect and our families are so happy!',
  },
  /*{
    image: '/assets/img/ravikant-netra.jpg',
    name: 'Seema & Dinesh',
    married: 'Married in 2024',
    quote:
      'We found our perfect match within 6 months. The personalized matching and family support made our journey smooth and meaningful.',
  },*/
  {
    image: '/assets/img/kirti-vighnesh.jpg',
    name: 'Vighnesh & Kirti',
    married: 'Married in 2016',
    quote:
      'The personalized guidance helped us at every step. We found a match whose family understood our culture and welcomed us with open arms.',
  },
  {
    image: '/assets/img/mrunali-vikram.jpg',
    name: 'Mrunali & Vikram',
    married: 'Married in 2011',
    quote:
      'We were nervous at first, but the warm and respectful community put us at ease. Within months, we met someone who truly felt like family.',
  }
] as const;

const STORIES = STORY_COPY.map((story, index) => ({
  ...story,
  id: `${story.name}-${index}`,
  image: storyImageUrl(story.image),
}));

function RingIcon() {
  return (
    <svg viewBox="0 0 32 32" className="ml-1.5 inline-block h-[0.72em] w-[0.72em] -translate-y-[0.08em]" aria-hidden="true">
      <circle cx="16" cy="19" r="8" fill="none" stroke="#C5CAD3" strokeWidth="1.8" />
      <path d="M16 11.2 18.4 7.2h-4.8L16 11.2Z" fill="#F4F7FB" stroke="#C5CAD3" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M16 7.6 17.1 5.4h-2.2L16 7.6Z" fill="#E7EDF6" stroke="#B7BEC9" strokeWidth="0.8" strokeLinejoin="round" />
    </svg>
  );
}

export default function HomeStoriesSection() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({
    active: false,
    moved: false,
    animating: false,
    startX: 0,
    scrollLeft: 0,
    lastX: 0,
    lastTime: 0,
    velocity: 0,
    frame: 0,
  });
  const [active, setActive] = useState(0);

  const maxScrollOf = (scroller: HTMLElement) => Math.max(0, scroller.scrollWidth - scroller.clientWidth);

  const indexForScroll = (scroller: HTMLElement, scrollLeft = scroller.scrollLeft) => {
    const last = STORIES.length - 1;
    const max = maxScrollOf(scroller);
    if (last <= 0 || max <= 1) return 0;
    const clamped = Math.min(max, Math.max(0, scrollLeft));
    if (clamped >= max - 2) return last;
    return Math.round((clamped / max) * last);
  };

  const scrollForIndex = (scroller: HTMLElement, index: number) => {
    const last = STORIES.length - 1;
    if (last <= 0) return 0;
    return (index / last) * maxScrollOf(scroller);
  };

  const syncActive = () => {
    const scroller = scrollerRef.current;
    if (!scroller || dragRef.current.active || dragRef.current.animating) return;
    setActive(indexForScroll(scroller));
  };

  const animateTo = (left: number, index: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    cancelAnimationFrame(dragRef.current.frame);
    const start = scroller.scrollLeft;
    const change = left - start;
    if (Math.abs(change) < 1) {
      scroller.scrollLeft = left;
      dragRef.current.animating = false;
      scroller.style.scrollBehavior = '';
      scroller.style.scrollSnapType = '';
      setActive(index);
      return;
    }

    const duration = 480;
    const startTime = performance.now();
    dragRef.current.animating = true;
    scroller.style.scrollBehavior = 'auto';
    scroller.style.scrollSnapType = 'none';

    const step = (now: number) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = 1 - (1 - progress) ** 3;
      scroller.scrollLeft = start + change * eased;
      if (progress < 1) {
        dragRef.current.frame = requestAnimationFrame(step);
        return;
      }
      dragRef.current.animating = false;
      scroller.style.scrollBehavior = '';
      scroller.style.scrollSnapType = '';
      setActive(index);
    };

    dragRef.current.frame = requestAnimationFrame(step);
  };

  const goTo = (index: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    animateTo(scrollForIndex(scroller, index), index);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    cancelAnimationFrame(dragRef.current.frame);
    dragRef.current = {
      active: true,
      moved: false,
      animating: false,
      startX: event.clientX,
      scrollLeft: scroller.scrollLeft,
      lastX: event.clientX,
      lastTime: performance.now(),
      velocity: 0,
      frame: 0,
    };
    scroller.style.scrollBehavior = 'auto';
    scroller.style.scrollSnapType = 'none';
    scroller.style.cursor = 'grabbing';
    if (scroller.setPointerCapture) {
      try {
        scroller.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is unavailable for this event. Drag still tracks move events on the row.
      }
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const now = performance.now();
    const delta = event.clientX - dragRef.current.startX;
    const stepX = event.clientX - dragRef.current.lastX;
    const stepTime = Math.max(now - dragRef.current.lastTime, 8);
    dragRef.current.velocity = stepX / stepTime;
    dragRef.current.lastX = event.clientX;
    dragRef.current.lastTime = now;
    if (Math.abs(delta) > 3) dragRef.current.moved = true;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    scroller.scrollLeft = Math.min(maxScroll, Math.max(0, dragRef.current.scrollLeft - delta));
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    const moved = dragRef.current.moved;
    dragRef.current.active = false;
    cancelAnimationFrame(dragRef.current.frame);
    const scroller = scrollerRef.current;
    if (scroller) {
      scroller.style.cursor = '';
      if (scroller.hasPointerCapture(event.pointerId)) {
        scroller.releasePointerCapture(event.pointerId);
      }
    }
    if (!moved || !scroller) {
      if (scroller) {
        scroller.style.scrollBehavior = '';
        scroller.style.scrollSnapType = '';
      }
      return;
    }

    const maxScroll = maxScrollOf(scroller);
    const projected = Math.min(maxScroll, Math.max(0, scroller.scrollLeft - dragRef.current.velocity * 180));
    const index = indexForScroll(scroller, projected);
    animateTo(scrollForIndex(scroller, index), index);
  };

  return (
    <section className="overflow-x-clip bg-[linear-gradient(180deg,#FFF0F6_0%,#F0F0FF_100%)] py-16 font-sans md:py-20">
      <div className="mx-auto max-w-[1120px] px-5 md:px-8">
        <div className="text-center">
          <p className="text-[13px] font-semibold uppercase tracking-[2px] text-black">Real stories</p>
          <h2 className="mt-3 whitespace-nowrap text-[clamp(18px,5.8vw,40px)] font-bold leading-tight text-black">
            10K+ couples said &ldquo;yes&rdquo;
            <RingIcon />
          </h2>
        </div>

        <div className="mt-10 flex flex-col gap-8 md:mt-14 lg:flex-row lg:items-center lg:gap-12">
          <div className="lg:w-[250px] lg:shrink-0">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/assets/img/quote-pink.svg"
              alt=""
              width={67}
              height={60}
              className="mb-6 block w-[52px] md:mb-8 md:w-[67px]"
              style={{ height: 'auto' }}
            />
            <h3 className="text-[28px] font-bold leading-tight text-black lg:max-w-[11ch] lg:text-[40px]">
              What our couples are saying
            </h3>
          </div>

          <div className="min-w-0 flex-1">
            <div className="-mr-5 md:-mr-8 lg:-mr-[max(2rem,calc((100vw-1120px)/2+2rem))]">
              <div
                ref={scrollerRef}
                onScroll={syncActive}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                className="scrollbar-hide flex cursor-grab touch-pan-y gap-4 overflow-x-auto scroll-smooth px-1 py-6"
              >
                {STORIES.map((story) => (
                  <article
                    key={story.id}
                    className="w-[calc(100%-52px)] shrink-0 rounded-[22px] border border-[#ece7f3] bg-white p-3 shadow-[0_8px_18px_-8px_rgba(70,40,90,0.22)] sm:w-[300px]"
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={story.image}
                      alt={story.name}
                      draggable={false}
                      width={435}
                      height={270}
                      className="pointer-events-none h-auto w-full rounded-[14px]"
                      loading="lazy"
                    />
                    <p className="mt-4 px-1 text-[14px] leading-6 text-neutral-800 md:text-[15px]">{story.quote}</p>
                    <p className="mt-4 px-1 text-[15px] font-semibold text-black">{story.name}</p>
                    <p className="mt-0.5 px-1 pb-1 text-[13px] text-neutral-500">{story.married}</p>
                  </article>
                ))}
              </div>
            </div>

            <div className="mt-2 flex items-center justify-center gap-2" role="tablist" aria-label="Success stories">
              {STORIES.map((story, index) => (
                <button
                  key={story.id}
                  type="button"
                  role="tab"
                  aria-selected={index === active}
                  aria-label={`Show ${story.name}`}
                  onClick={() => goTo(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === active ? 'w-2.5 bg-[#6B39FF]' : 'w-2 bg-[#ddd6f0]'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
